import { useGuideIcons } from "@expert/contentful";
import type { Logger } from "@expert/logging";
import { Accordion, ActionIcon, Button, Center, Flex, Group, Image, Stack, Text, Transition } from "@mantine/core";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { IconChevronDown } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { CloseIcon } from "../assets";
import { HomeIcon } from "../assets/HomeIcon";
import type { GuideSelectionObject } from "../logic-builder";
import { triggerNextStep } from "../logic-builder";
import { getGuideAnalyticData, useGuideStore } from "../store";
import type { CloseGuideSource } from "../types";
import classes from "./GuideCard.module.css";

interface OpenedCardProps {
    guides: GuideSelectionObject[];
    onGuideSelection: (guide: string) => void;
    stepId?: string;
}
interface GuideSelectionCardProps {
    callSid?: string;
    logger: Logger;
    onGuideClose: (source: CloseGuideSource) => void;
    opened: boolean;
    stepId?: string;
}

function SelectionCardHeader({ onClose }: { onClose: (source: CloseGuideSource) => void }): React.JSX.Element {
    return (
        <Center>
            <Accordion.Control>
                <Group gap="xs" wrap="nowrap">
                    <HomeIcon />
                    <Text fw={700} size="sm">
                        Home
                    </Text>
                </Group>
            </Accordion.Control>
            <ActionIcon color="gray" onClick={() => onClose("closeButton")} size="lg" variant="subtle">
                <CloseIcon />
            </ActionIcon>
        </Center>
    );
}

function OpenedCard({ guides, onGuideSelection, stepId }: OpenedCardProps): React.JSX.Element {
    const isCompactView = guides.length > 4;
    const iconMap = useGuideIcons(guides.map((guide) => guide.icon));
    return (
        <Accordion.Panel display="flex">
            <Group align="flex-start" gap="md" grow={!isCompactView} w="100%">
                {guides.map((guide) => {
                    const { icon, isDisabled, title } = guide;

                    const iconUrl = iconMap.get(icon);
                    return (
                        <Flex
                            h={isCompactView ? "4rem" : "100%"}
                            justify="center"
                            key={`${stepId}-${title}`}
                            w={isCompactView ? "calc(25% - var(--mantine-spacing-sm))" : undefined}
                        >
                            <Button
                                className={classes.guideSelectionButton}
                                h="100%"
                                disabled={isDisabled}
                                onClick={() => onGuideSelection(title)}
                                p="sm"
                                radius="sm"
                                variant="outline"
                                w="100%"
                            >
                                <Stack align="center" gap="0">
                                    {!isCompactView && iconUrl && <Image h={36} src={iconUrl} w={36} mb="sm" />}
                                    <Text className={classes.selectionButtonText} size="xs">
                                        {title}
                                    </Text>
                                    {isDisabled && (
                                        <Text size="xs" component="footer">
                                            (Coming soon)
                                        </Text>
                                    )}
                                </Stack>
                            </Button>
                        </Flex>
                    );
                })}
            </Group>
        </Accordion.Panel>
    );
}

export function GuideSelectionCard({
    callSid,
    logger: loggerProp,
    onGuideClose,
    opened,
    stepId,
}: GuideSelectionCardProps): React.JSX.Element | null {
    const { availableGuides: guides } = useGuideStore();
    const logger = useMemo(
        () => loggerProp.child({ module: "GuideSelectionCard", supportTeam: "solve" }),
        [loggerProp],
    );
    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const [collapseValue, setCollapseValue] = React.useState<string | null>("opened");
    const { currentStepId } = getGuideAnalyticData();

    function handleCollapseChange(value: string | null) {
        logger.info(`Solve Guide | Collapse value changed to: ${value ?? "closed"}`);
        setCollapseValue(value);
        void enhancedDispatcher.dispatchBusinessEvent("Guide_CollapseChange", {
            value: value ?? "closed",
        });
    }

    function handleGuideSelection(guide: string) {
        logger.info(`Solve Guide | Selected flow: ${guide}`);

        void enhancedDispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
            currentStepId,
            guide,
            source: "manual",
        });
        triggerNextStep(guide, logger);
    }

    return (
        <Transition mounted={opened} transition="fade-down" duration={400} timingFunction="ease">
            {(styles) => (
                <Accordion
                    chevron={<IconChevronDown viewBox="0 0 20 20" />}
                    chevronPosition="left"
                    className={classes.guideCard}
                    classNames={{ content: classes.accordionContent }}
                    h="auto"
                    w="100%"
                    maw="50rem"
                    style={{ ...styles }}
                    onChange={(value) => handleCollapseChange(value)}
                    value={collapseValue}
                >
                    <Accordion.Item
                        className={classes.accordionItem}
                        display="flex"
                        p="sm"
                        pt="xs"
                        pb="xs"
                        value="opened"
                    >
                        <SelectionCardHeader onClose={onGuideClose} />
                        <OpenedCard guides={guides} onGuideSelection={handleGuideSelection} stepId={stepId} />
                    </Accordion.Item>
                </Accordion>
            )}
        </Transition>
    );
}
